import {
  type ComponentProps,
  type FocusEvent,
  forwardRef,
  type MouseEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  KibSearch,
  type KibSuperSelectOption,
} from '@chewy/kib-fields-new-react'
import { KibCloseIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import { useDebounce } from 'use-debounce'
import { Defaults } from '@pbt/pbt-ui-components'

import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import styles from './KibSearchWrapper.module.scss'

// Custom ref interface that matches InlineSearchHandle
export interface KibSearchHandle {
  clearSearch: () => void
}

type KibSearchWrapperProps = Omit<
  ComponentProps<typeof KibSearch>,
  'onChange' | 'onFocus' | 'onBlur' | 'options' | 'label'
> & {
  /**
   * When true (AWO_CHARGES_R5_3 charge search): clear immediately notifies parent + blurs; close button tracks raw text.
   * When false: legacy debounced close visibility and imperative clear only clears local input.
   */
  immediateClearSync?: boolean
  label?: string
  onBlur: (event: FocusEvent<HTMLInputElement>) => void
  onChange: (value: string) => void
  onClear?: (event: MouseEvent<HTMLButtonElement>) => void
  onFocus: (event: FocusEvent<HTMLInputElement>) => void
  options?: (string | KibSuperSelectOption)[]
  showCloseButton?: boolean
}

export const KibSearchWrapper = forwardRef<
  KibSearchHandle,
  KibSearchWrapperProps
>(
  (
    {
      value,
      onChange,
      onClear,
      placeholder,
      disabled,
      onFocus,
      onBlur,
      label,
      options,
      showCloseButton = false,
      immediateClearSync = false,
      ...rest
    },
    ref,
  ) => {
    const [rawSearchTerm, setRawSearchTerm] = useState(value ?? '')
    const isClearing = useRef(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const hasVisibleInputText = Boolean((rawSearchTerm ?? '').trim())

    const blurComboboxInput = () => {
      rootRef.current
        ?.querySelector<HTMLInputElement>('input[role="combobox"]')
        ?.blur()
    }

    const [searchTerm] = useDebounce(
      rawSearchTerm,
      Defaults.DEBOUNCE_ACTION_TIME,
    )

    useEffect(() => {
      const trimmedSearchTerm = searchTerm?.trim() || ''
      const currentValue = (value ?? '').trim()

      if (isClearing.current) {
        if (trimmedSearchTerm !== '') {
          return
        }
        isClearing.current = false
      }

      if (trimmedSearchTerm !== currentValue) {
        onChange(trimmedSearchTerm)
      }
    }, [searchTerm, onChange, value])

    useEffect(() => {
      setRawSearchTerm(value ?? '')
    }, [value])

    useImperativeHandle(
      ref,
      () => ({
        clearSearch: () => {
          if (immediateClearSync) {
            isClearing.current = true
            setRawSearchTerm('')
            onChange('')
            blurComboboxInput()
          } else {
            setRawSearchTerm('')
          }
        },
      }),
      [immediateClearSync, onChange],
    )

    const handleChange = (inputValue: string | KibSuperSelectOption) => {
      const newValue =
        typeof inputValue === 'string' ? inputValue : inputValue?.value || ''
      setRawSearchTerm(newValue)
    }

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      onFocus(event)
    }

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      onBlur(event)
    }

    const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
      isClearing.current = true
      setRawSearchTerm('')
      if (immediateClearSync) {
        onChange('')
      }
      onClear?.(event)
      if (immediateClearSync) {
        blurComboboxInput()
      }
    }

    const showCloseButtonVisible =
      showCloseButton &&
      (immediateClearSync ? hasVisibleInputText : Boolean(searchTerm))

    return (
      <div
        className={classNames(styles.searchWrapper, {
          [styles.showCloseButton]: showCloseButtonVisible,
        })}
        data-kib-search-only="true"
        ref={immediateClearSync ? rootRef : undefined}
      >
        <KibSearch
          disabled={disabled}
          label={label ?? ''}
          options={options ?? []}
          placeholder={placeholder}
          value={rawSearchTerm}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          {...rest}
        />
        {showCloseButtonVisible && onClear && (
          <button
            aria-label="Clear"
            className={styles.closeButton}
            type="button"
            onClick={handleClear}
          >
            <KibCloseIcon />
          </button>
        )}
      </div>
    )
  },
)
